import * as sha512 from 'js-sha512';
import axios from 'axios';
import * as Yup from 'yup';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { API_SERVER } from '../../../config/constant';
import useCityStore from '../../../infrastructure/store/useCityStore';

export default function LoginForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const setApiurl = useCityStore(state => state.setApiurl);
  const setEmail = useCityStore(state => state.setEmail);
  const setRegion = useCityStore(state => state.setRegion);
  const setLatitude = useCityStore(state => state.setLatitude);
  const setLongitude = useCityStore(state => state.setLongitude);
  const setPermission = useCityStore(state => state.setPermission);

  const initialValues = {
    email: '',
    password: '',
    remember: true
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email('電子信箱格式錯誤').required('必填'),
    password: Yup.string().required('必填')
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      await Login(values);
    }
  });

  const Login = async (values) => {
    const loginObj = {
      "Account": values.email,
      "Password": sha512(values.password)
    };
    const options = {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      data: JSON.stringify(loginObj),
      url: `${API_SERVER}api/user/login`
    };
    try {
      const response = await axios(options);
      if (response.data.code === 0) {

        const { apiurl, email, region, Latitude, Longitude, permission } = response.data.data;
        await Promise.all([
          localStorage.setItem('apiurl', apiurl),
          localStorage.setItem('email', email),
          localStorage.setItem('region', region),
          localStorage.setItem('Latitude', Latitude),
          localStorage.setItem('Longitude', Longitude),
          localStorage.setItem('permission', JSON.stringify(permission))
        ]);
        // 確保這些函數正確地設置了 useCityStore 的狀態
        await Promise.all([
          setApiurl(apiurl),
          setEmail(email),
          setRegion(region),
          setLatitude(Latitude),
          setLongitude(Longitude),
          setPermission(permission)
        ]);
        window.location.assign("/app");
      } else {
        setErrorMessage('登入失敗，請重試');
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage('登入過程中發生錯誤，請稍後再試');
    }
  };

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = useCallback(() => {
    setShowPassword((show) => !show);
  }, []);

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            autoComplete="username"
            type="email"
            label="電子信箱"
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            label="密碼"
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
        </Stack>

        {errorMessage && (
          <Stack sx={{ mt: 2 }}>
            <Typography color="error">{errorMessage}</Typography>
          </Stack>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
          {/* Add any additional elements here if needed */}
        </Stack>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
        >
          登入
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
}